/* eslint-disable import/no-cycle */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import Button from '@mui/material/Button';
import GlobalDialog from './Dialog';
import GlobalTitle from './Title';
import Close from '../../../images/global/Close.svg';

const ConfirmationDialog = ({
  open,
  handleClose,
  message = '',
  children = null,
  handleConfirm,
  intl,
  title = null,
  cancelLabel = null,
  confirmLabel = null,
  cancelClassName = 'big-flat-button-red',
  confirmClassName = 'big-flat-button-green',
  confirmDisabled = false,
  closeOnConfirm = true,
}) => {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  const onConfirm = async () => {
    if (submitting || confirmDisabled) return;
    setSubmitting(true);
    try {
      await handleConfirm();
      if (closeOnConfirm) {
        handleClose();
        // Keep submitting true until `open` becomes false (useEffect reset),
        // so confirm can't be double-clicked during close.
      } else {
        // Caller is keeping the dialog open (or closing itself). Re-enable
        // actions so a swallowed error / retry path isn't stuck disabled.
        setSubmitting(false);
      }
    } catch (error) {
      // Keep dialog open so the user can retry or cancel.
      setSubmitting(false);
    }
  };

  const onClose = () => {
    if (submitting) return;
    handleClose();
  };

  return (
    <GlobalDialog
      modal={false}
      open={open}
      autoDetectWindowHeight={false}
      contentClassName="confirmation-dialog"
      onClose={onClose}
    >
      <GlobalTitle
        title={title || intl.formatMessage({ defaultMessage: 'Confirmation' })}
      />
      <div className="ccd-content">
        {message?.split('\n')?.map((item, index) => (
          <p key={`k-${index + 1}`}>{item}</p>
        ))}
        {children}
      </div>
      <div className="ccd-action">
        <Button
          className={cancelClassName}
          onClick={onClose}
          disabled={submitting}
          style={{ marginRight: '20px' }}
        >
          {cancelLabel || intl.formatMessage({ defaultMessage: 'No' })}
        </Button>
        <Button
          className={confirmClassName}
          disabled={confirmDisabled || submitting}
          onClick={onConfirm}
        >
          {confirmLabel || intl.formatMessage({ defaultMessage: 'Yes' })}
        </Button>
      </div>
      <Close onClick={onClose} className="ccd-close-button" />
    </GlobalDialog>
  );
};

ConfirmationDialog.propTypes = {
  message: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool.isRequired,
  handleConfirm: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  title: PropTypes.node,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelClassName: PropTypes.string,
  confirmClassName: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  closeOnConfirm: PropTypes.bool,
};

export default injectIntl(ConfirmationDialog);
